import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import { MDBCollapse, MDBBtn, MDBBadge, MDBCard, MDBCardHeader, MDBCardBody, MDBRow, MDBCol } from 'mdb-react-ui-kit'
import { UIInputToggleSwitch } from './../../../../components/forms/form-fields'
import PaginationControls from './../../../../components/content/PaginationControls/PaginationControls.component'
import SupportEmailsModal from './../../../../components/modals/SupportEmailsModal/SupportEmailsModal.component'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import JiraService from './../../../../shared/services/Jira.service'
import moment from 'moment'

import './MySupportTickets.component.scss'

const MySupportTickets = ({ perPage }) => {
  perPage = perPage && !isNaN(perPage) ? perPage : 5

  const [ticketView, setTicketView] = useState('open'),
    userId = UserProfileService.getUserId(),
    isAdmin = UserProfileService.isA(['internal-admin', 'internal-staff', 'agency-owner', 'system-admin'], true),
    [issueKey, setIssueKey] = useState(null),
    [isLoading, setIsLoading] = useState(false),
    [isFetching, setIsFetching] = useState(false),
    [tickets, setTickets] = useState(false),
    [ticket, setTicket] = useState(false)

  const fetchTicket = async issueKey => {
    setIsFetching(true)

    setTicket(issueKey ? await JiraService.ticket(issueKey) : null)

    setIsFetching(false)
  }

  const getTickets = () => {
    let page = tickets?.pagination?.page || 0,
      min = page > 0 ? page * 5 : 0,
      max = parseInt(min) + perPage

    return (Array.isArray(tickets && tickets?.models) ? tickets.models : []).slice(min, max)
  }

  const renderStatus = status => {
    switch (`${status || ''}`.trim().toLowerCase()) {
      case 'resolved':
        return (
          <MDBBadge className='ms-2' color='success'>
            {status}
          </MDBBadge>
        )
      case 'waiting for support':
      case 'escalated':
        return (
          <MDBBadge className='ms-2' color='danger'>
            {status}
          </MDBBadge>
        )
      case 'waiting for customer':
        return (
          <MDBBadge className='ms-2' color='info'>
            {status}
          </MDBBadge>
        )
      case 'pending':
      case 'in progress':
      case 'in-progress':
        return (
          <MDBBadge className='ms-2' color='warning'>
            {status}
          </MDBBadge>
        )
      case 'closed':
      case 'canceled':
        return (
          <MDBBadge className='ms-2' color='info'>
            {status}
          </MDBBadge>
        )
      default:
        return <></>
    }
  }

  const renderTicket = () => {
    if (issueKey !== ticket?.key) {
      if (isFetching)
        return (
          <div className='ticket-details is-fetching'>
            <div className='ticket-loading-wrapper'>
              <LoadingSpinner size='md' isActive={true} label='Loading Support Ticket' />
            </div>

            <MDBBtn type='button' color='default' size='sm' onClick={() => setIssueKey(null)} className='hide-btn'>
              Hide Details
            </MDBBtn>
          </div>
        )

      return <>Loading ...</>
    }

    return (
      <div className='ticket-details'>
        <div className='description'>
          <label>Description:</label>&nbsp;{ticket.description}
        </div>
        <table>
          <tbody>
            <tr>
              <td>Status:</td>
              <td>{renderStatus(ticket.status)}</td>
            </tr>

            <tr>
              <td>Link:</td>
              <td>
                <a href={isAdmin ? ticket.admin_link : ticket.customer_link} target='_BLANK' rel='noopener noreferrer'>
                  {isAdmin ? ticket.admin_link : ticket.customer_link}
                </a>
              </td>
            </tr>

            <tr>
              <td>Reporter:</td>
              <td>{ticket.reporter}</td>
            </tr>

            <tr>
              <td>Support Rep:</td>
              <td>{ticket.assignee}</td>
            </tr>

            <tr>
              <td>Ticket Participants:</td>
              <td>{ticket.participants}</td>
            </tr>

            <tr>
              <td>Created At:</td>
              <td>{ticket.created}</td>
            </tr>

            <tr>
              <td>Project:</td>
              <td>{ticket.project}</td>
            </tr>

            <tr>
              <td>Update At:</td>
              <td>{ticket.updated}</td>
            </tr>

            <tr>
              <td>Created By:</td>
              <td>{ticket.creator}</td>
            </tr>

            <tr>
              <td>Created At:</td>
              <td>{ticket.created}</td>
            </tr>
          </tbody>
        </table>
        <MDBBtn type='button' color='default' size='sm' onClick={() => setIssueKey(null)} className='hide-btn'>
          Hide Details
        </MDBBtn>
      </div>
    )
  }

  const renderPreview = ticket => {
    let updated = `${ticket?.updated || ''}`.split(' ').shift()
    updated = updated ? moment(updated, 'YYYY/MM/DD').format('MM/DD/YYYY') : ''

    return (
      <div className='ticket-preview'>
        <div className='details'>
          <div className='assignee'>
            Support Agent: <span>{ticket.assignee}</span>
          </div>
          {updated ? (
            <div className='timestamp'>
              Last Updated: <span>{updated}</span>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>
    )
  }

  useEffect(() => {
    const fetchTickets = async () => {
      setIsLoading(true)

      const t = userId ? await JiraService.myTickets(ticketView) : []
      setTickets({
        models: t,
        pagination: { per_page: perPage, total: t.length, page: 0 },
      })

      setIsLoading(false)
    }

    fetchTickets()
  }, [userId, ticketView, perPage])

  useEffect(() => {
    fetchTicket(issueKey)
  }, [issueKey])

  return (
    <MDBCard id='MySupportTicketsComponent'>
      <MDBCardHeader>
        <MDBRow>
          <MDBCol size='12' sm='12' md='6'>
            My Support Tickets
          </MDBCol>
          <MDBCol size='12' sm='12' md='6'>
            <MDBRow className='ticket-controls'>
              {isAdmin ? (
                <MDBCol>
                  <div className='manage-emails-btn'>
                    <SupportEmailsModal buttonLabel='Manage Support Emails' buttonSize='sm' buttonColor='indigo' />
                  </div>
                </MDBCol>
              ) : (
                <></>
              )}
              <MDBCol>Viewing:</MDBCol>
              <MDBCol>
                <UIInputToggleSwitch
                  disabled={false}
                  labelOn={false ? <i className='fa fa-spin fa-spinner'></i> : 'Open Tickets'}
                  labelOff={false ? <i className='fa fa-spin fa-spinner'></i> : 'Closed Tickets'}
                  value={ticketView === 'open'}
                  onChange={evt => {
                    setTicketView(ticketView === 'open' ? 'closed' : 'open')
                  }}
                />
              </MDBCol>
            </MDBRow>
          </MDBCol>
        </MDBRow>
      </MDBCardHeader>
      <MDBCardBody>
        {isLoading ? (
          <div className='all-ticket-loading-wrapper'>
            <LoadingSpinner size='md' isActive={true} label='Loading My Support Tickets' />
          </div>
        ) : getTickets().length === 0 ? (
          <div className='no-tickets-message'>
            <i className='fa fa-ticket-alt fa-2x mb-3'></i>
            <p>You have no {ticketView} support tickets.</p>
          </div>
        ) : (
          <>
            <ul className='tickets-list'>
              {getTickets().map((ticket, idx) => (
                <li key={`support-ticket-${tickets?.pagination?.page || ''}-${idx}`}>
                  <div className='support-ticket'>
                    <div className='summary'>
                      {renderStatus(ticket.status)}
                      <span onClick={() => setIssueKey(ticket.key === issueKey ? null : ticket.key)}>
                        ({ticket.key})&nbsp;{ticket.summary}
                      </span>
                    </div>
                    <MDBCollapse open={ticket.key === issueKey}>{renderTicket(ticket)}</MDBCollapse>
                    <MDBCollapse open={ticket.key !== issueKey}>{renderPreview(ticket)}</MDBCollapse>
                  </div>
                </li>
              ))}
            </ul>
            <div className='pagination-wrapper'>
              <PaginationControls
                pagination={tickets.pagination}
                onPageSelect={pagination => setTickets({ ...tickets, pagination })}
              />
            </div>
          </>
        )}
      </MDBCardBody>
    </MDBCard>
  )
}

export default observer(MySupportTickets)
